// Search: the API shapes (see webapp.py /api/search*) and the query-string helpers the pages share.
// Everything is one query string (see search.py); facets and the advanced form only add or remove tokens.
import type { NoticeItem } from "./types";

export type Sort = "best" | "due" | "newest";
export const SORTS: [Sort, string][] = [
  ["best", "Best match"],
  ["due", "Due soonest"],
  ["newest", "Newest"],
];
export const FACET_NAMES: Record<string, string> = {
  kind: "Record",
  work: "Kind of work",
  path: "How it’s bought",
  due: "Due",
  posted: "Posted",
  stage: "Stage",
  type: "Type",
  topic: "Topic",
  buyer: "Buyer",
  setaside: "Set-aside",
  source: "Found on",
};
export type Frequency = "off" | "daily" | "instant";
export const FREQ: [Frequency, string, string][] = [
  ["off", "Off", "Only in GovChew"],
  ["daily", "Daily digest", "One email each morning"],
  ["instant", "As they arrive", "An email after each check"],
];
export const EXAMPLES = [
  "counter-uas training",
  '"intelligent tutoring"',
  "stage:rfi buyer:navy due:30d",
  "naics:5417 posted:7d",
  "sol:W912",
  "simulation -buyer:army type:baa",
];

export interface FacetValue {
  value: string;
  label: string;
  count: number;
  active: boolean;
  query: string;
}

export interface Chip {
  name: string;
  value: string;
  label: string;
  negated: boolean;
  query: string;
}

export interface SearchResult extends Omit<NoticeItem, "also"> {
  solicitation_id: string;
  snippet: string;
  also: string[];
  tracked: boolean;
  record: string;
}

export interface SearchResponse {
  query: string;
  sort: Sort;
  total: number;
  offset: number;
  items: SearchResult[];
  facets: Record<string, FacetValue[]>;
  chips: Chip[];
  highlight: string[];
  hidden: { closed: { count: number; query: string }; sole: { count: number; query: string } };
  has_text: boolean;
  records: number;
}

export interface SuggestNotice {
  key: string;
  title: string;
  solicitation_id: string;
  buyer: string;
  deadline: string | null;
  agency: string;
}

export interface SuggestResponse {
  ids: SuggestNotice[];
  notices: SuggestNotice[];
  filters: { name: string; value: string; label: string; count: number }[];
  saved: { id: number; name: string; query: string }[];
}

export interface OptionValue {
  value: string;
  label: string;
  parent: string;
  sub?: string;  // a line under the label in pickers: a vehicle's scope, a source's site
  url?: string;  // where a source publishes
}
export type SearchOptions = Record<string, OptionValue[]>;

// Routes: /search?q=…&sort=…, /advanced?q=…, /saved/:id?q=…&sort=…
export function searchPath(q: string, sort?: string | null): string {
  const p = new URLSearchParams();
  if (q) p.set("q", q);
  if (sort && sort !== "best") p.set("sort", sort);
  const s = p.toString();
  return "/search" + (s ? "?" + s : "");
}

/** Inside a saved search, refinements stay attached to it so they can be saved back. */
export function resultsPath(q: string, sort: string | null | undefined, savedId?: number | null): string {
  if (!savedId) return searchPath(q, sort);
  const p = new URLSearchParams({ q });
  if (sort && sort !== "best") p.set("sort", sort);
  return `/saved/${savedId}?${p}`;
}

export function recentSearches(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem("govchew.recent") || "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function remember(q: string): void {
  if (!q) return;
  try {
    const r = [q, ...recentSearches().filter((x) => x !== q)].slice(0, 6);
    localStorage.setItem("govchew.recent", JSON.stringify(r));
  } catch {
    // storage blocked: recent searches are a convenience
  }
}

const FILTER_FIELD =
  /^-?(buyer|stage|type|topic|setaside|source|naics|psc|aln|sol|due|posted|is|in|kind|work|path|shape|bought|agency|customer|instrument|coi|set-aside|solicitation|id|status|category|won|winner|awardee):/i;

export function stripFilters(q: string): string {
  return (q.match(/(-?)(?:([a-z-]+):)?(?:"([^"]*)"|(\S+))/gi) || []).filter((t) => !FILTER_FIELD.test(t)).join(" ");
}

export function suggestName(q: string, chips: Chip[] = []): string {
  const words = stripFilters(q).replace(/"/g, "").trim();
  const name =
    (words ? words[0]!.toUpperCase() + words.slice(1) : "") +
    (chips.length ? (words ? " · " : "") + chips.slice(0, 2).map((c) => c.label).join(", ") : "");
  return name || "My search";
}

// Advanced search: parse a query into form fields and compose it back.
export const ADV_FACETS: [string, string][] = [
  ["work", "Kind of work"],
  ["path", "How it’s bought"],
  ["who", "Who can bid"],
  ["flag", "Labels"],
  ["stage", "Stage"],
  ["type", "Type"],
  ["topic", "Topic"],
  ["setaside", "Set-aside"],
  ["kind", "Record"],
  ["funded", "Paid from"],
  ["source", "Found on"],
];

export interface AdvancedForm {
  all: string;
  phrase: string;
  any: string;
  none: string;
  where: "any" | "title";
  sol: string;
  naics: string;
  psc: string;
  aln: string;
  won: string;  // who won it: part of a company's name
  buyer: string[];
  notBuyer: string[];  // buyers to leave out
  status: "open" | "any" | "closed";
  sole: boolean;
  due: string;
  dueFrom: string;
  dueTo: string;
  posted: string;
  postedFrom: string;
  postedTo: string;
  nots: string[];
  facets: Record<string, string[]>;
}

interface Token {
  neg: boolean;
  field: string;
  quoted: boolean;
  value: string;
}

function tokenize(q: string): Token[] {
  return [...(q || "").matchAll(/(-?)(?:([a-z-]+):)?(?:"([^"]*)"|(\S+))/gi)].map((m) => ({
    neg: m[1] === "-",
    field: (m[2] || "").toLowerCase(),
    quoted: m[3] !== undefined,
    value: (m[3] ?? m[4] ?? "") as string,
  }));
}

export function parseForForm(q: string): AdvancedForm {
  const f: AdvancedForm = {
    all: "",
    phrase: "",
    any: "",
    none: "",
    where: "any",
    sol: "",
    naics: "",
    psc: "",
    aln: "",
    won: "",
    buyer: [],
    notBuyer: [],
    status: "open",
    sole: false,
    due: "",
    dueFrom: "",
    dueTo: "",
    posted: "",
    postedFrom: "",
    postedTo: "",
    nots: [],
    facets: Object.fromEntries(ADV_FACETS.map(([n]) => [n, [] as string[]])),
  };
  const all: string[] = [];
  const any: string[] = [];
  const none: string[] = [];
  const toks = tokenize(q);
  for (let i = 0; i < toks.length; i++) {
    const t = toks[i]!;
    if (t.field) {
      const v = t.value.toLowerCase();
      if (t.neg && t.field === "buyer") {
        f.notBuyer.push(v);
        continue;
      }
      if (t.neg) {
        f.nots.push(`-${t.field}:${t.value.includes(" ") ? '"' + t.value + '"' : t.value}`);
        continue;
      }
      if (t.field === "buyer") f.buyer.push(v);
      else if (t.field === "won" || t.field === "winner" || t.field === "awardee") f.won = (f.won ? f.won + ", " : "") + t.value;
      else if (f.facets[t.field]) f.facets[t.field]!.push(v);
      else if (t.field === "sol" || t.field === "naics" || t.field === "psc" || t.field === "aln")
        f[t.field] = (f[t.field] ? f[t.field] + ", " : "") + t.value;
      else if (t.field === "in" && v === "title") f.where = "title";
      else if (t.field === "is") {
        if (v === "any") f.status = "any";
        else if (v === "closed") f.status = "closed";
        else if (v === "solesource") f.sole = true;
      } else if (t.field === "due" || t.field === "posted") {
        const m = v.match(/^(\d{4}-\d{2}-\d{2})?\.\.(\d{4}-\d{2}-\d{2})?$/);
        if (m) {
          f[t.field] = "range";
          f[`${t.field}From`] = m[1] || "";
          f[`${t.field}To`] = m[2] || "";
        } else f[t.field] = v;
      } else f.nots.push(`${t.field}:${t.value}`);
      continue;
    }
    if (t.neg) {
      none.push(t.quoted ? `"${t.value}"` : t.value);
      continue;
    }
    if (toks[i + 1]?.value === "OR" && !toks[i + 1]!.quoted) {
      const group = [t.value];
      while (toks[i + 1]?.value === "OR" && toks[i + 2]) {
        group.push(toks[i + 2]!.value);
        i += 2;
      }
      any.push(...group);
      continue;
    }
    if (t.quoted && t.value.includes(" ") && !f.phrase) {
      f.phrase = t.value;
      continue;
    }
    all.push(t.quoted ? `"${t.value}"` : t.value);
  }
  f.all = all.join(" ");
  f.any = any.join(" ");
  f.none = none.join(" ");
  return f;
}

export function composeQuery(f: AdvancedForm): string {
  const out: string[] = [];
  const qv = (v: string) => (/\s/.test(v) ? `"${v}"` : v);
  const words = (s: string) => (s || "").match(/"[^"]*"|\S+/g) || [];
  out.push(...words(f.all));
  if (f.phrase.trim()) out.push(`"${f.phrase.trim().replace(/"/g, "")}"`);
  const any = words(f.any);
  if (any.length) out.push(any.join(" OR "));
  out.push(...words(f.none).map((w) => "-" + w.replace(/^-/, "")));
  if (f.where === "title") out.push("in:title");
  for (const n of ["sol", "naics", "psc", "aln"] as const)
    for (const v of (f[n] || "").split(/[,\s]+/).filter(Boolean)) out.push(`${n}:${v}`);
  for (const v of (f.won || "").split(",").map((x) => x.trim()).filter(Boolean)) out.push(`won:${qv(v)}`);
  for (const v of f.buyer) out.push(`buyer:${qv(v)}`);
  for (const v of f.notBuyer) out.push(`-buyer:${qv(v)}`);
  for (const [n] of ADV_FACETS) for (const v of f.facets[n] || []) out.push(`${n}:${qv(v)}`);
  for (const n of ["due", "posted"] as const) {
    if (f[n] === "range") {
      const from = f[`${n}From`];
      const to = f[`${n}To`];
      if (from || to) out.push(`${n}:${from}..${to}`);
    } else if (f[n]) out.push(`${n}:${f[n]}`);
  }
  if (f.status !== "open") out.push(`is:${f.status}`);
  if (f.sole) out.push("is:solesource");
  out.push(...f.nots);
  return out.join(" ");
}

export const human = (s: string | null | undefined): string => String(s || "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
